import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  effect,
  inject,
  input,
  output,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { SideMenuComponent } from '../side-menu/side-menu.component';

/** Modo de presentacion de la barra lateral. */
export type SidebarMode = 'full' | 'rail' | 'overlay';

/**
 * Barra lateral fija de la consola.
 *
 * · `full`    → 270 px con etiquetas.
 * · `rail`    → 87 px, solo iconos con tooltip.
 * · `overlay` → cajon (drawer) movil: sale de fuera de pantalla, es un
 *               `role="dialog"` modal y se cierra con Escape o tocando el velo.
 *
 * Es una superficie de PRIMER NIVEL: aqui si va `backdrop-filter`.
 */
@Component({
  selector: 'app-side-bar',
  imports: [RouterLink, SideMenuComponent],
  templateUrl: './side-bar.component.html',
  styleUrl: './side-bar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.is-rail]': 'mode() === "rail"',
    '[class.is-overlay]': 'mode() === "overlay"',
    '[class.is-open]': 'mode() === "overlay" && open()',
    '[attr.role]': 'mode() === "overlay" ? "dialog" : null',
    '[attr.aria-modal]': 'mode() === "overlay" ? "true" : null',
    '[attr.aria-label]': 'mode() === "overlay" ? "Menú de navegación" : null',
    '[attr.inert]': 'mode() === "overlay" && !open() ? "" : null',
  },
})
export class SideBarComponent {
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  readonly mode = input<SidebarMode>('full');

  /** Solo aplica en modo `overlay`. */
  readonly open = input(false);

  /** Pide colapsar/expandir el rail (escritorio). */
  readonly railToggle = output<void>();

  /** Pide cerrar el cajon (movil). */
  readonly closeRequest = output<void>();

  /** Se propaga desde el menu: navegar cierra el cajon. */
  readonly navigated = output<void>();

  protected readonly isRail = computed(() => this.mode() === 'rail');
  protected readonly isOverlay = computed(() => this.mode() === 'overlay');

  protected readonly railLabel = computed(() =>
    this.isRail() ? 'Expandir la barra lateral' : 'Colapsar la barra lateral',
  );

  constructor() {
    // Al abrir el cajon, el foco entra en el; el layout lo devuelve al cerrar.
    effect(() => {
      if (this.mode() !== 'overlay' || !this.open()) return;

      queueMicrotask(() => {
        const target = this.elementRef.nativeElement.querySelector<HTMLElement>(
          '[data-autofocus]',
        );
        target?.focus();
      });
    });
  }
}
